import EditIcon from '@mui/icons-material/Edit'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, IconButton, Stack } from '@mui/material'
import { FC, ReactNode } from 'react'
import { Location } from '../../../../api/models'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'
import { ALL_PERMITTING_SCOPES } from '../../../../constants'
import { AgencyTypeValues } from '../../../../constants/AgencyType'
import {
  displayAgencyPermitRequirement,
  getExpirationDateText,
  getPermitNumber,
  getStatusActionPrompt,
  hasStatusAction
} from '../../../../utils/permitUtils'
import { GridColumn } from './GridColumn'
import { PermitExpiredCellContents } from './PermitExpiredCellContents'
import { getCallVerification, getGuardPolicy, getRenewalCadence, reformatValuesToYesOrNo } from '../JurisdictionDetailsTab/jurisdictionUtils'
import { AppFormLink } from '../JurisdictionDetailsTab/AppFormLink'
import { OrdinanceLink } from '../JurisdictionDetailsTab/OrdinanceLink'

interface PermitColumnViewerProps {
  agencyType: AgencyTypeValues
  location: Location
  col: number
  editingAgencyType: AgencyTypeValues | null
  onEditClicked: () => void
  actionButtons?: ReactNode
}

export const PermitColumnViewer: FC<PermitColumnViewerProps> = ({ agencyType, location, col, editingAgencyType, onEditClicked, actionButtons }) => {
  const permit = location.permits ? location.permits[agencyType] : undefined
  const authContext = useAuthenticationContext()
  const userCanEditPermits = authContext.hasAllScopes(ALL_PERMITTING_SCOPES)
  const statusActionNeeded = hasStatusAction(location, 'fire') || hasStatusAction(location, 'police')

  return (
    <GridColumn col={col} shouldShowBackground={hasStatusAction(location, agencyType)}>
      {/* agency type header */}
      <Box
        sx={{ padding: 'var(--spacing-s)', position: 'relative', width: '100%' }}
        data-nobackground
        data-testid={`${agencyType}-permit-column-header`}
      >
        <Box sx={{ position: 'absolute', right: '100%' }}>
          <PermitExpiredCellContents agencyType={agencyType} location={location} />
        </Box>
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', fontWeight: 500 }}>
            {agencyType.toUpperCase()}
            {userCanEditPermits && (
              <IconButton onClick={() => onEditClicked()} data-testid={`${agencyType}-edit-permit-button`}>
                <EditIcon color="primary" />
              </IconButton>
            )}
          </Stack>
          {!!editingAgencyType && <Box>{actionButtons}</Box>}
        </Stack>
      </Box>

      {statusActionNeeded && (
        <Stack
          data-testid={`${agencyType}-permit-prompt`}
          direction="row"
          spacing={'var(--spacing-2xs)'}
          alignItems="center"
          data-noborder
          useFlexGap
          sx={{ padding: 'var(--spacing-m) var(--spacing-m) var(--spacing-l) 0', color: 'var(--text-error)' }}
        >
          {hasStatusAction(location, agencyType) && (
            <>
              <ErrorOutlineIcon />
              {getStatusActionPrompt(location, agencyType)}
            </>
          )}
        </Stack>
      )}

      <Box sx={{ padding: 'var(--spacing-s)' }} data-testid={`${agencyType}-permit_number`}>
        <Stack sx={{ fontSize: 'var(--size-s)', fontWeight: 300 }}>{getPermitNumber(permit)}</Stack>
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-expiration`}>
        {getExpirationDateText(permit)}
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_cadence`}>
        {location.jurisdiction ? getRenewalCadence(location.jurisdiction, agencyType) : 'N/A'}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_requirement`}>
        {displayAgencyPermitRequirement(location, agencyType)}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_form_link`}>
        <AppFormLink agencyType={agencyType} location={location} />
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_ordinance_link`}>
        <OrdinanceLink agencyType={agencyType} location={location} />
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_verified_response`}>
        {reformatValuesToYesOrNo(location.jurisdiction?.agencyRules[agencyType]?.verifiedResponseRequirements?.raw.code)}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_multiple_alarm_triggers`}>
        {reformatValuesToYesOrNo(location.jurisdiction?.agencyRules[agencyType]?.multipleAlarmTriggersRequirements?.raw?.code)}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_call_verification`}>
        {location.jurisdiction ? getCallVerification(location.jurisdiction, agencyType) : 'N/A'}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_guard_service`}>
        {location.jurisdiction ? getGuardPolicy(location.jurisdiction) : 'N/A'}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_additional_notes`}>
        {location.jurisdiction?.agencyRules[agencyType]?.permitAdditionalNotes || 'N/A'}
      </Box>
    </GridColumn>
  )
}
